import { ReservationReturn } from '../clients/rust-api-client';

/**
 * What only a REST read of a booking carries: the sync engine's copy never
 * holds them (back-end-rs/docs/SYNC_ENGINE.md §6) — a door code,
 * manager-only notes, and what the server works out per read.
 */
export const REST_ONLY = [
  'access_code',
  'access_instructions',
  'access_visible_from_local',
  'internal_notes',
  'cancel_outcome_now',
  'check_in_opens_at_local',
  'check_in_closes_at_local',
  'documents_outstanding',
  'fee_refundable_until_local',
  'agreement_version',
] as const;

/** The detail page's own REST read or write answer, and the copy as it
 *  stood when that arrived. */
export interface OwnReservation {
  row: ReservationReturn;
  copy: ReservationReturn | null | undefined;
}

/**
 * The booking the detail page shows (#561). The synced copy renders at
 * once and follows changes made anywhere; the page's own REST read adds
 * what the copy never holds. Right after the page wrote — a decision, an
 * operation — its answer is shown whole until the engine's echo changes
 * the copy, so the change does not flick back in between.
 */
export const shownReservation = (
  synced: ReservationReturn | null | undefined,
  own: OwnReservation | null
): ReservationReturn | null => {
  if (!synced) return own?.row ?? null;
  if (!own || own.row.id !== synced.id) return synced;
  if (own.copy === synced) return own.row;
  const extra = Object.fromEntries(REST_ONLY.map((k) => [k, own.row[k]]));
  return { ...synced, ...extra } as ReservationReturn;
};
